/** How far the lightbox image enlarges when zoomed. */
const LIGHTBOX_ZOOM = 2.5;

/** Pointer travel (px) past which a press on the zoomed image is a pan, not a click. */
const DRAG_THRESHOLD = 5;

if (!customElements.get('product-accordions')) {
  customElements.define(
    'product-accordions',
    class ProductAccordions extends HTMLElement {
      drag = null;

      connectedCallback() {
        document.addEventListener('product:variant-change', this.onVariantChange);
        this.addEventListener('click', this.onClick);

        this.lightbox = this.querySelector('.product-accordions__lightbox');
        this.viewport = this.lightbox?.querySelector('.product-accordions__lightbox-viewport');
        this.viewport?.addEventListener('pointerdown', this.onPointerDown);
        this.lightbox?.addEventListener('close', this.onLightboxClose);
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.onVariantChange);
        this.removeEventListener('click', this.onClick);
        this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
        this.lightbox?.removeEventListener('close', this.onLightboxClose);
      }

      /**
       * Opens a nutrition label in the lightbox, or closes the lightbox on a
       * backdrop click (the click lands on the <dialog> itself, not its content).
       * @param {MouseEvent} event
       */
      onClick = (event) => {
        const { lightbox } = this;
        if (!lightbox) return;

        if (event.target === lightbox) {
          lightbox.close();
          return;
        }

        if (event.target.closest('.product-accordions__lightbox-zoom')) {
          this.setZoom(!lightbox.hasAttribute('data-zoomed'));
          return;
        }

        // The viewport, not the image: while dragging, pointer capture retargets
        // the click to the viewport.
        if (event.target.closest('.product-accordions__lightbox-viewport')) {
          // The end of a drag-to-pan also fires a click; that one isn't a toggle.
          if (this.drag?.moved) return;
          this.setZoom(!lightbox.hasAttribute('data-zoomed'), event);
          return;
        }

        const trigger = event.target.closest('[data-lightbox-src]');
        if (!trigger) return;

        lightbox.querySelector('.product-accordions__lightbox-image').src = trigger.dataset.lightboxSrc;
        lightbox.showModal();
      };

      /**
       * Zooms the lightbox image in or out. Zooming in from a click keeps the
       * clicked point under the pointer; from the button, it zooms on the centre.
       * @param {boolean} zoomed
       * @param {MouseEvent} [event] - The click on the image, if any
       */
      setZoom(zoomed, event) {
        const { lightbox, viewport } = this;
        const image = viewport.querySelector('.product-accordions__lightbox-image');
        const button = lightbox.querySelector('.product-accordions__lightbox-zoom');

        let focusX = 0.5;
        let focusY = 0.5;
        if (event) {
          const rect = image.getBoundingClientRect();
          focusX = (event.clientX - rect.left) / rect.width;
          focusY = (event.clientY - rect.top) / rect.height;
        }

        lightbox.toggleAttribute('data-zoomed', zoomed);
        button.setAttribute('aria-pressed', String(zoomed));
        button.setAttribute('aria-label', zoomed ? button.dataset.labelZoomOut : button.dataset.labelZoomIn);

        if (!zoomed) return;

        viewport.style.setProperty('--product-accordions-lightbox-zoom', LIGHTBOX_ZOOM);
        const pointX = event ? event.clientX - viewport.getBoundingClientRect().left : viewport.clientWidth / 2;
        const pointY = event ? event.clientY - viewport.getBoundingClientRect().top : viewport.clientHeight / 2;
        viewport.scrollLeft = focusX * image.offsetWidth - pointX;
        viewport.scrollTop = focusY * image.offsetHeight - pointY;
      }

      /** Drag-to-pan for mice; touch and trackpads already scroll the viewport. */
      onPointerDown = (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.lightbox.hasAttribute('data-zoomed')) {
          this.drag = null;
          return;
        }

        const { viewport } = this;
        this.drag = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop, moved: false };

        const onMove = (moveEvent) => {
          const dx = moveEvent.clientX - this.drag.x;
          const dy = moveEvent.clientY - this.drag.y;
          if (!this.drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
            this.drag.moved = true;
            // Captured only once it is a drag, so a plain click keeps its own target.
            viewport.setPointerCapture(event.pointerId);
          }
          if (!this.drag.moved) return;

          viewport.scrollLeft = this.drag.left - dx;
          viewport.scrollTop = this.drag.top - dy;
        };
        const onUp = () => {
          viewport.removeEventListener('pointermove', onMove);
          viewport.removeEventListener('pointerup', onUp);
          viewport.removeEventListener('pointercancel', onUp);
        };
        viewport.addEventListener('pointermove', onMove);
        viewport.addEventListener('pointerup', onUp);
        viewport.addEventListener('pointercancel', onUp);
      };

      /** Each label opens unzoomed. */
      onLightboxClose = () => {
        this.setZoom(false);
        this.drag = null;
      };

      /**
       * Shows, in each variant-specific row slot, the entry for the new variant,
       * or the slot's fallback entry when none matches. The open state moves
       * with it so switching flavour doesn't collapse the row being read.
       * @param {CustomEvent} event - product:variant-change from section-product.js
       */
      onVariantChange = (event) => {
        const variantId = event.detail?.variant?.id;
        if (!variantId) return;

        // Quick-shop modals for other products dispatch the same event.
        const variantToken = `,${variantId},`;
        if (!this.dataset.variantIds.includes(variantToken)) return;

        const groups = new Set();
        for (const row of this.querySelectorAll('[data-variant-group]')) {
          groups.add(row.dataset.variantGroup);
        }

        for (const group of groups) {
          const rows = [...this.querySelectorAll(`[data-variant-group="${group}"]`)];
          const incoming =
            rows.find((row) => row.dataset.variantIds.includes(variantToken)) ??
            rows.find((row) => row.hasAttribute('data-variant-fallback'));
          const outgoing = rows.find((row) => !row.hidden);
          if (!incoming || incoming === outgoing) continue;

          const wasOpen = outgoing?.open ?? false;
          if (outgoing) {
            outgoing.open = false;
            outgoing.hidden = true;
          }

          incoming.hidden = false;
          incoming.open = wasOpen;
        }
      };
    }
  );
}
